import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Package, Clock, MessageCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { OrderChat } from '../../components/OrderChat';

export default function RestaurantOrders() {
  const { api } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [chatOrderId, setChatOrderId] = useState(null);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders');
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      await api.put(`/orders/${orderId}/status`, { status });
      toast.success(`Order marked as ${status}`);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const getFilteredOrders = () => {
    if (statusFilter === 'all') return orders;
    return orders.filter(order => order.status === statusFilter);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      confirmed: 'bg-blue-100 text-blue-700 border-blue-200',
      preparing: 'bg-purple-100 text-purple-700 border-purple-200',
      ready: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      in_transit: 'bg-orange-100 text-orange-700 border-orange-200',
      delivered: 'bg-green-100 text-green-700 border-green-200',
      cancelled: 'bg-red-100 text-red-700 border-red-200',
    };
    return colors[status] || '';
  };

  const getNextStatus = (status) => {
    const next = {
      pending: 'confirmed',
      confirmed: 'preparing',
      preparing: 'ready',
      ready: 'picked_up',
    };
    return next[status];
  };

  const getButtonLabel = (status) => {
    const labels = {
      pending: 'Accept',
      confirmed: 'Start Preparing',
      preparing: 'Mark Ready',
      ready: 'Picked Up',
    };
    return labels[status] || '';
  };

  const renderOrderItems = (items) => {
    const elements = [];
    for (let i = 0; i < items.length; i++) {
      elements.push(
        <p key={i}>{items[i].quantity}x {items[i].name}</p>
      );
    }
    return elements;
  };

  const renderOrders = () => {
    const filteredOrders = getFilteredOrders();
    if (filteredOrders.length === 0) {
      return (
        <div className="text-center py-16 bg-white rounded-xl col-span-full">
          <Package className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">No orders</h2>
          <p className="text-slate-500">New orders will appear here</p>
        </div>
      );
    }

    const orderCards = [];
    for (let i = 0; i < filteredOrders.length; i++) {
      const order = filteredOrders[i];
      const nextStatus = getNextStatus(order.status);
      
      orderCards.push(
        <div 
          key={order.id} 
          className={`bg-white rounded-xl p-5 shadow-sm border-2 ${getStatusColor(order.status)}`}
          data-testid={`order-card-${order.id}`}
        >
          <div className="flex justify-between items-start mb-4">
            <div>
              <p className="font-bold text-lg">#{order.id.slice(0, 8)}</p>
              <p className="text-sm text-slate-500">{formatTime(order.created_at)}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
              {order.status}
            </span>
          </div>

          <div className="space-y-2 mb-4">
            <p className="font-medium text-slate-900">{order.customer_name}</p>
            <div className="text-sm text-slate-500">
              {renderOrderItems(order.items)}
            </div>
          </div>

          <div className="flex justify-between items-center pt-4 border-t border-slate-100">
            <span className="font-bold text-lg text-orange-500">£{order.total.toFixed(2)}</span>
            
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                className="text-orange-500 border-orange-200 hover:bg-orange-50"
                onClick={() => setChatOrderId(order.id)}
                data-testid={`chat-${order.id}`}
              >
                <MessageCircle className="w-4 h-4" />
              </Button>
              
              {nextStatus && (
                <Button
                  size="sm"
                  className="btn-primary"
                  onClick={() => updateOrderStatus(order.id, nextStatus)}
                  data-testid={`update-${order.id}`}
                >
                  {getButtonLabel(order.status)}
                </Button>
              )}

              {order.status === 'pending' && (
                <Button
                  size="sm"
                  variant="outline"
                  className="text-red-500 border-red-200"
                  onClick={() => updateOrderStatus(order.id, 'cancelled')}
                >
                  Reject
                </Button>
              )}
            </div>
          </div>
        </div>
      );
    }
    return orderCards;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-slate-900">Orders</h1>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="order-filter">
            <SelectValue placeholder="All Orders" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Orders</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="confirmed">Confirmed</SelectItem>
            <SelectItem value="preparing">Preparing</SelectItem>
            <SelectItem value="ready">Ready</SelectItem>
            <SelectItem value="in_transit">In Transit</SelectItem>
            <SelectItem value="delivered">Delivered</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-6 animate-pulse">
            <div className="h-6 bg-slate-200 rounded w-1/2 mb-4"></div>
            <div className="h-20 bg-slate-200 rounded"></div>
          </div>
          <div className="bg-white rounded-xl p-6 animate-pulse">
            <div className="h-6 bg-slate-200 rounded w-1/2 mb-4"></div>
            <div className="h-20 bg-slate-200 rounded"></div>
          </div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {renderOrders()}
        </div>
      )}

      {/* Order Chat Modal */}
      <OrderChat 
        orderId={chatOrderId}
        isOpen={!!chatOrderId}
        onClose={() => setChatOrderId(null)}
        participants={{ customer: true, restaurant: true, driver: true }}
      />
    </div>
  );
}
